<script>
    import { onMount } from 'svelte';
    import Table from './Table.svelte';

    let data = [];
    let loading = true;
    let error = null;
    let currentPage = 1;
    let totalPages = 1;
    let headers = [];
    let headerOptions = {};

    export let url;

    const fetchData = async (page = 1) => {
        loading = true;
        try {
            // console.log(`${url}?page=${page}`);
            const response = await fetch(`${url}?page=${page}`);

            if (!response.ok) throw new Error('Failed to fetch data');

            const result = await response.json();

            headers = Object.keys(result.schools[0]);
            headerOptions = result.header_options;
            data = result.schools.map((row) => Object.values(row));

            // Use the pagination data
            totalPages = result.total_pages;
            currentPage = result.current_page;

            // Check if there is a next or previous page
            hasNextPage = !!result.next_page;
            hasPrevPage = !!result.prev_page;
        } catch (err) {
            error = err.message;
        } finally {
            loading = false;
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            fetchData(newPage);
        }
    };

    const generatePageNumbers = (currentPage, totalPages) => {
        const pages = [];
        const maxDisplayedPages = 20; // Adjust based on how many pages you want to display at once
        const sidePages = Math.floor((maxDisplayedPages - 1) / 2); // Pages to show on each side of the current page

        if (totalPages <= maxDisplayedPages) {
            // If total pages are less than or equal to the max displayed pages, show all
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            if (currentPage <= sidePages + 1) {
                // Show the first set of pages when the current page is near the start
                for (let i = 1; i <= sidePages + 1; i++) pages.push(i);
                pages.push('...');
                pages.push(totalPages);
            } else if (currentPage > totalPages - sidePages - 1) {
                // Show the last set of pages when the current page is near the end
                pages.push(1);
                pages.push('...');
                for (let i = totalPages - sidePages; i <= totalPages; i++) pages.push(i);
            } else {
                // Show the pages around the current page
                pages.push(1);
                pages.push('...');
                for (let i = currentPage - sidePages; i <= currentPage + sidePages; i++) pages.push(i);
                pages.push('...');
                pages.push(totalPages);
            }
        }
        return pages;
    };

    onMount(() => {
        fetchData();
    });
</script>

{#if data}
    <div role="navigation" aria-label="Pagination" class="pagination">
        <button on:click={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
            &larr; Previous
        </button>

        {#each generatePageNumbers(currentPage, totalPages) as page}
            {#if page === '...'}
                <span class="ellipsis">...</span>
            {:else if page === currentPage}
                <span class="current">{page.toLocaleString()}</span>
            {:else}
                <button on:click={() => handlePageChange(page)}>{page.toLocaleString()}</button>
            {/if}
        {/each}

        <button on:click={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
            Next &rarr;
        </button>
    </div>

    <Table {headers} {headerOptions} rows={data} />

    <div role="navigation" aria-label="Pagination" class="pagination">
        <button on:click={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
            &larr; Previous
        </button>

        {#each generatePageNumbers(currentPage, totalPages) as page}
            {#if page === '...'}
                <span class="ellipsis">...</span>
            {:else if page === currentPage}
                <span class="current">{page.toLocaleString()}</span>
            {:else}
                <button on:click={() => handlePageChange(page)}>{page.toLocaleString()}</button>
            {/if}
        {/each}

        <button on:click={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
            Next &rarr;
        </button>
    </div>
{:else if loading}
    Loading…
{:else if error}
    <div>Error: {error}</div>
{/if}

<style>
    .pagination {
        display: flex;
        width: 100%;
        align-items: center;
        justify-content: center;
        padding: 1rem 0;
        font-size: var(--scale-1);
        list-style: none;
    }

    a,
    button {
        padding: 0.5rem 0.75rem;
        border: 1px solid transparent;
        border-radius: 4px;
        margin: 0 0.25rem;
        background: none;
        color: #06aee0;
        font-size: var(--scale-1);
        font-variant: oldstyle-nums;
        text-decoration: none;
    }

    a:hover,
    button:hover {
        border: 1px solid;
        border-color: #ddd;
        background-color: #f8f9fa;
        text-decoration: none;
    }

    button:disabled {
        color: #6c757d;
        cursor: not-allowed;
        pointer-events: none;
    }

    .current {
        padding: 0.5rem 0.75rem;
        color: var(--color-text-body);
        font-style: normal;
        font-weight: bold;
    }
</style>
